import {
  CreateBase,
  Form,
  useGetIdentity,
  useListContext,
  useNotify,
  useRecordContext,
  useResourceContext,
  useCreate,
  useRefresh,
  useTranslate,
  useUpdate,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/admin/form";
import { cn } from "@/lib/utils";

import { NoteInputs } from "./NoteInputs";
import { getCurrentDate } from "./utils";
import { foreignKeyMapping } from "./foreignKeyMapping";
import { crmErrorMessage } from "../misc/crmErrorMessage";
import { NoteSignalInput } from "../temperature/NoteSignalInput";
import { NOTE_SIGNAL_FIELD, splitNoteSignal } from "../temperature/noteSignal";

export const NoteCreate = ({
  reference,
  showStatus,
  className,
}: {
  reference: "contacts" | "deals";
  showStatus?: boolean;
  className?: string;
}) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (!record || !identity) return null;

  const defaultStatus = reference === "contacts" ? record.status : undefined;

  return (
    <CreateBase resource={resource} redirect={false}>
      <Form>
        <div className={cn("space-y-3", className)}>
          <NoteInputs defaultStatus={defaultStatus} showStatus={showStatus} />
          {reference === "contacts" ? <NoteSignalInput /> : null}
          <NoteCreateButton
            defaultStatus={defaultStatus}
            record={record}
            reference={reference}
          />
        </div>
      </Form>
    </CreateBase>
  );
};

const NoteCreateButton = ({
  defaultStatus,
  reference,
  record,
}: {
  defaultStatus?: string;
  reference: "contacts" | "deals";
  record: RaRecord<Identifier>;
}) => {
  const [update] = useUpdate();
  const [create] = useCreate();
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { reset, getValues } = useFormContext();
  const { refetch } = useListContext();

  if (!record || !identity) return null;

  const resetValues: {
    date: string;
    text: null;
    attachments: null;
    status?: string;
    [NOTE_SIGNAL_FIELD]?: string;
  } = {
    date: getCurrentDate(),
    text: null,
    attachments: null,
  };

  // [hiperautomacao] A note that is also a purchase signal is recorded as
  // a signal too, so the temperature is recalculated by the database.
  const recordSignal = (signalType: string, text: unknown) => {
    create(
      "purchase_signals",
      {
        data: {
          contact_id: record.id,
          type: signalType,
          note: typeof text === "string" && text.trim() ? text.trim() : null,
        },
      },
      {
        onSuccess: () => {
          notify("hiper.temperature.signal.saved_with_note", {
            type: "success",
          });
          refresh();
        },
        onError: (error) => {
          notify(crmErrorMessage(error), { type: "error" });
        },
      },
    );
  };

  const handleSuccess = (data: any) => {
    const signalType =
      reference === "contacts"
        ? splitNoteSignal({
            [NOTE_SIGNAL_FIELD]: getValues(NOTE_SIGNAL_FIELD),
          }).signalType
        : null;
    if (reference === "contacts") {
      resetValues.status = data.status ?? defaultStatus;
      resetValues[NOTE_SIGNAL_FIELD] = "";
    }

    reset(resetValues, { keepValues: false });
    refetch();
    update(reference, {
      id: (record && record.id) as unknown as Identifier,
      data: {
        last_seen:
          reference === "contacts" ? new Date().toISOString() : undefined,
        status: data.status,
      },
      previousData: record,
    });
    if (signalType) {
      recordSignal(signalType, data.text);
      return;
    }
    notify("resources.notes.added", {
      messageArgs: {
        _: "Note added",
      },
    });
  };

  return (
    <div className="flex justify-end">
      <SaveButton
        type="button"
        label={translate("resources.notes.action.add_this")}
        transform={(data) => ({
          ...splitNoteSignal(data).note,
          [foreignKeyMapping[reference]]: record.id,
          sales_id: identity.id,
          date: new Date(data.date || getCurrentDate()).toISOString(),
        })}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      />
    </div>
  );
};
